import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, EMPTY, finalize } from 'rxjs';
import { PriceCurrencyPipe } from '@/common/pipes/price-currency.pipe';
import { FormatDateTimePipe } from '@/common/pipes/format-date-time.pipe';
import { DriversLedgerApiService, DriverLedgerRow } from './_api/drivers-ledger.api.service';
import { NavbarBridgeService } from '@/common/navbar/navbar-bridge.service';
import { restoreFromUrl } from './_utils/restore-from-url';
import { DriverLedgerSearchResultsComponent } from './driver-ledger-search-results/driver-ledger-search-results.component';

@Component({
	selector: 'app-drivers-ledger',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatTableModule,
		MatProgressSpinnerModule,
		MatCardModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatPaginatorModule,
		MatSortModule,
		PriceCurrencyPipe,
		FormatDateTimePipe,
		DriverLedgerSearchResultsComponent,
	],
	providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }],
	templateUrl: './drivers-ledger.component.html',
	styleUrl: './drivers-ledger.component.scss',
})
export class DriversLedgerComponent implements OnInit {
	readonly displayedColumns = ['driver', 'totalPln', 'totalEur', 'lastUpdated'];

	rows = signal<DriverLedgerRow[]>([]);
	totalRecords = signal(0);
	sumPln = signal(0);
	sumEur = signal(0);
	isLoading = signal(false);

	readonly currentPage = signal(1);
	readonly pageSize = signal(20);

	readonly driverFilter = signal('');
	readonly plnMin = signal<number | null>(null);
	readonly plnMax = signal<number | null>(null);
	readonly eurMin = signal<number | null>(null);
	readonly eurMax = signal<number | null>(null);

	readonly searchQuery = signal('');

	readonly driverFilterControl = new FormControl<string>('');
	readonly plnMinControl = new FormControl<number | null>(null);
	readonly plnMaxControl = new FormControl<number | null>(null);
	readonly eurMinControl = new FormControl<number | null>(null);
	readonly eurMaxControl = new FormControl<number | null>(null);
	readonly onlyWithEntriesControl = new FormControl<boolean>(false);
	readonly searchControl = new FormControl<string>('', { nonNullable: true });

	readonly onlyWithEntries = signal(false);

	readonly sortBy = signal<string | null>(null);
	readonly sortDirection = signal<'asc' | 'desc' | null>(null);

	get hasActiveFilters(): boolean {
		return !!(
			this.driverFilter() ||
			this.plnMin() !== null ||
			this.plnMax() !== null ||
			this.eurMin() !== null ||
			this.eurMax() !== null ||
			this.onlyWithEntries()
		);
	}

	private readonly apiService = inject(DriversLedgerApiService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly destroyRef = inject(DestroyRef);
	private readonly navbarBridge = inject(NavbarBridgeService);

	ngOnInit(): void {
		this.navbarBridge.setTitle('Kasa kierowców');
		restoreFromUrl(this.route.snapshot.queryParams, this);
		this.bindFilter(this.driverFilterControl, (v) => this.driverFilter.set((v ?? '').trim()));
		this.bindFilter(this.plnMinControl, (v) => this.plnMin.set(v));
		this.bindFilter(this.plnMaxControl, (v) => this.plnMax.set(v));
		this.bindFilter(this.eurMinControl, (v) => this.eurMin.set(v));
		this.bindFilter(this.eurMaxControl, (v) => this.eurMax.set(v));
		this.bindFilter(this.onlyWithEntriesControl, (v) => this.onlyWithEntries.set(v ?? false), false);

		this.searchControl.valueChanges
			.pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => this.searchQuery.set(value.trim()));

		this.loadData();
	}

	clearSearch(): void {
		this.searchControl.setValue('');
	}

	onPageChange(event: PageEvent): void {
		this.pageSize.set(event.pageSize);
		this.currentPage.set(event.pageIndex + 1);
		this.syncUrl();
		this.loadData();
	}

	clearFilters(): void {
		this.driverFilterControl.setValue('');
		this.plnMinControl.setValue(null);
		this.plnMaxControl.setValue(null);
		this.eurMinControl.setValue(null);
		this.eurMaxControl.setValue(null);
		this.onlyWithEntriesControl.setValue(false);
	}

	onSortChange(sort: Sort): void {
		this.sortBy.set(sort.direction ? sort.active : null);
		this.sortDirection.set(sort.direction || null);
		this.currentPage.set(1);
		this.syncUrl();
		this.loadData();
	}

	openDriver(row: DriverLedgerRow): void {
		this.router.navigate(['/drivers', row.driverId], { queryParams: { tab: 'ledger' } });
	}

	private bindFilter<T>(control: FormControl<T>, setter: (v: T) => void, withDebounce = true): void {
		const source$ = withDebounce ? control.valueChanges.pipe(debounceTime(400)) : control.valueChanges;
		source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
			setter(v);
			this.currentPage.set(1);
			this.syncUrl();
			this.loadData();
		});
	}

	private syncUrl(): void {
		const params: Record<string, string | number> = {};
		if (this.driverFilter()) params['driver'] = this.driverFilter();
		if (this.plnMin() !== null) params['plnMin'] = this.plnMin()!;
		if (this.plnMax() !== null) params['plnMax'] = this.plnMax()!;
		if (this.eurMin() !== null) params['eurMin'] = this.eurMin()!;
		if (this.eurMax() !== null) params['eurMax'] = this.eurMax()!;
		if (this.onlyWithEntries()) params['onlyWithEntries'] = 1;
		if (this.sortBy()) params['sortBy'] = this.sortBy()!;
		if (this.sortDirection()) params['sortDirection'] = this.sortDirection()!;
		if (this.currentPage() !== 1) params['page'] = this.currentPage();
		if (this.pageSize() !== 20) params['perPage'] = this.pageSize();
		this.router.navigate([], { queryParams: params, replaceUrl: true });
	}

	private loadData(): void {
		this.isLoading.set(true);
		this.apiService
			.getAllSummary({
				page: this.currentPage(),
				perPage: this.pageSize(),
				name: this.driverFilter() || undefined,
				plnMin: this.plnMin() ?? undefined,
				plnMax: this.plnMax() ?? undefined,
				eurMin: this.eurMin() ?? undefined,
				eurMax: this.eurMax() ?? undefined,
				onlyWithEntries: this.onlyWithEntries() || undefined,
				sortBy: this.sortBy() ?? undefined,
				sortDirection: this.sortDirection() ?? undefined,
			})
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((page) => {
				this.rows.set(page.items);
				this.totalRecords.set(page.total);
				this.sumPln.set(page.sumPln);
				this.sumEur.set(page.sumEur);
			});
	}
}
